import React, { useEffect, useState, useRef } from 'react';
import { notification, Popover, Space } from 'antd';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import './assets/style.css';
import { useQuery } from 'react-query';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { PageHeader } from 'components/Atom/PageHeader';
import { ResetButton, FilterButton, Button } from 'components/Atom/Button';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import Filter from './assets/filter';
import SearchField from 'components/Atom/SearchField/index';
import { getInvoices, getInvoicesExport } from 'api/invoices';
import { TIMEZONES, USER_ROLES } from 'utils/constant';
import { checkUserPermissions, formatAmount, handleDateRangeFormat, handleStateByPassData } from 'utils/services';
import { formatInTimeZone } from 'date-fns-tz';
import StatusTag from 'components/Atom/StatusTag';
import { useNavigate, useLocation } from 'react-router-dom';

const MerchantInvoice = () => {
  const store = useSelector((state) => state?.company?.store);
  const [filterToggle, setFilterToggle] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [recordId, setRecordId] = useState('');
  const [delayTimer, setDelayTimer] = useState();
  const [exportLoading, setExportLoading] = useState(false);
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const timezone = store?.settings?.timezone || TIMEZONES?.PKT;

  const [search, setSearch] = useState('');

  const fetchData = async (queryParams) => {
    try {
      const paramsData = { offset: queryParams?.offset, limit: queryParams?.limit };
      if (queryParams?.search !== '') {
        paramsData['search_key'] = queryParams?.search?.trim();
      }
      const formattedData = filtersDataValues(queryParams?.filtersData);
      const params = { ...paramsData, ...formattedData, ...(store?.isSelected && { store_id: store?._id }) };
      const response = await getInvoices(params);
      return response?.data;
    } catch (err) {
      console.log(err);
    }
  };
  const { data, refetch, isLoading, isFetching } = useQuery('invoices', () => fetchData(queryParams), {
    enabled: false,
    manual: true,
  });
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 20,
    search: '',
    currentPage: 1,
  });
  const [trigerApi, setTrigerApi] = useState(true);
  const columnsData = [
    {
      title: 'Invoice #',
      render: (value) => value?._id || 'N/A',
      key: '_id',
      fixed: 'left',
    },
    {
      title: 'Amount',
      render: (value) => `${formatAmount(value?.invoice_net_amount || '0', 'PKR')}`,
      key: 'amount',
    },
    {
      title: 'Created Time',
      render: (value) => (value?.createdAt ? formatInTimeZone(value?.createdAt, timezone, 'MMM d, yyyy h:mm:ss a') : 'N/A'),
      key: 'createdAt',
    },
    {
      title: 'Status',
      render: (value) => <StatusTag tag={value?.status} />,
      key: 'status',
    },
  ];

  useEffect(() => {
    if (location?.state?.isRedirection && data) {
      setSearch(location?.state?.searchValue);
      setRecordId(location?.state?.id);
      navigate(location?.pathname, { replace: true });
      const data = handleStateByPassData(location?.state?.filtersData, true);
      if (!data) {
        refetch();
      }
      setQueryParams(data);
      setTimeout(() => {
        if (tableContainerRef?.current && location?.state?.id) {
          const row = document?.querySelector(`.custom-row-class.highlighted-row`);
          if (row) {
            row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 10);
      return;
    } else if (location?.state?.isRedirection) {
      navigate(location?.pathname, { replace: true });
    }
    refetch();
  }, [trigerApi, store]);
  const filtersDataValues = (data) => {
    let filtersData = {};
    if (data?.date_range) {
      const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
      const { start_date, end_date } = handleDateRangeFormat(data?.date_range, timezone);
      filtersData.start_date = start_date;
      filtersData.end_date = end_date;
    }
    filtersData.status = data?.invoice_status ?? null;
    return filtersData;
  };
  const getSearch = async (e) => {
    if (e === '' || e == null) {
      notification?.error({ message: 'Please enter details for search', placement: 'bottomRight' });
    } else {
      setQueryParams((pre) => ({ ...pre, currentPage: 1, offset: 0, search: e }));
      setTrigerApi(!trigerApi);
    }
  };

  const handlePagePageChange = (page, pageSize) => {
    if (queryParams?.limit !== pageSize) {
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: 0, currentPage: 1 }));
      setTrigerApi(!trigerApi);
    } else {
      const countOffset = page * queryParams?.limit - queryParams?.limit;
      setQueryParams((pre) => ({ ...pre, limit: pageSize, offset: countOffset, currentPage: page }));
      setTrigerApi(!trigerApi);
    }
  };
  const handleSearchData = () => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      handleRefresh();
    }, 1500);
    setDelayTimer(newTimer);
  };
  const handleRefresh = (isRefresh = true) => {
    if (isRefresh) {
      setSearch(null);
    } else {
      setIsFilterModalOpen(false);
    }
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, search: '', filtersData: isRefresh ? pre?.filtersData : null }));
    setTrigerApi(!trigerApi);
  };
  const handleCloseFilterModel = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };
  const handleFiltersSearch = (data) => {
    setQueryParams((pre) => ({ ...pre, offset: 0, currentPage: 1, filtersData: data }));
    setTrigerApi(!trigerApi);
  };
  const filterToggler = () => {
    setFilterToggle(true);
  };
  const handleReset = () => {
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, filtersData: {} }));
    setTrigerApi(!trigerApi);
    setIsFilterModalOpen(false);
  };
  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '700', color: '#262626' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <div className='intentFilters'>
        <Filter filterToggler={filterToggler} filtersData={queryParams?.filtersData} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} />
      </div>
    </div>
  );
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Wallet Invoices',
      },
    ],
  };
  const handleRowHover = () => {
    setRecordId('');
  };
  const handleSearch = (e) => {
    if (delayTimer && e !== '' && e !== null) {
      clearTimeout(delayTimer);
    }
    setSearch(e?.target?.value);
  };
  const convertToCSV = (data) => {
    if (!data?.length) return '';

    const headers = ['Invoice #', 'Account ID', 'Business Name', 'Amount', 'Frequency', 'Created Time', 'Status'];

    const rows = data?.map?.((item) => [
      item?._id || 'N/A',
      item?.account_id || 'N/A',
      item?.merchant_id?.businessName || 'N/A',
      item?.invoice_net_amount || 'N/A',
      item?.frequency === 't1' ? 'T+1' : item?.frequency === 't2' ? 'T+2' : item?.frequency === 't3' ? 'T+3' : item?.frequency || 'N/A',
      formatInTimeZone(item?.createdAt, 'Asia/Karachi', 'MMM d yyyy h:mm:ss a'),
      item?.status || 'N/A',
    ]);

    return [headers?.join?.(','), ...rows?.map?.((row) => row?.join?.(','))]?.join?.('\n');
  };
  const handleExport = async () => {
    try {
      if (!store?.isSelected) {
        notification?.error({
          message: 'Store Selection Required',
          description: 'Please select a store from the drop-down menu above to proceed export',
          placement: 'bottomRight',
        });
        return;
      }

      setExportLoading(true);
      const paramsData = {};
      if (queryParams?.search !== '') {
        paramsData['search_key'] = queryParams?.search?.trim?.();
      }
      const formattedData = filtersDataValues(queryParams?.filtersData);
      const exportParams = { ...paramsData, ...formattedData, store_id: store?._id };

      const response = await getInvoicesExport(exportParams);
      const invoices = response?.data?.invoices || [];

      if (!invoices?.length) {
        notification?.error({
          message: 'No Data',
          description: 'No invoices available to export',
          placement: 'bottomRight',
        });
        return;
      }

      const csv = convertToCSV(invoices);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document?.createElement('a');
      const url = URL?.createObjectURL(blob);

      const fileName = `Invoices_${store?.name}`;

      link?.setAttribute('href', url);
      link?.setAttribute('download', fileName);
      document?.body?.appendChild(link);
      link?.click();
      link?.parentNode?.removeChild(link);

      notification?.success({
        message: 'Invoices Exported',
        description: 'Invoices exported successfully',
        placement: 'bottomRight',
      });
    } catch (error) {
      console.error('Export error:', error?.message || error);
      notification?.error({
        message: 'Export Error',
        description: 'Failed to export invoices',
        placement: 'bottomRight',
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={{ padding: '0px', width: '100%' }}>
      <div className='header-container'>
        <Breadcrumbs {...breadcrumbProps} />
        <div className='headerPage'>
          <div className='mb-2'>
            <PageHeader label='Wallet Invoices' button={<ResetButton onClick={isFetching ? '' : handleRefresh} loading={isFetching} />} />
          </div>
          <div className='flex txn-btn'>
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
              <Button loading={exportLoading} disabled={exportLoading} className='custom-btns p-2 flex items-center' onClick={handleExport}>
                {exportLoading === true ? null : <DownloadOutlined className='inline text-base' />} <span className='text-base'>Export</span>
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className='actionPanel'>
        <div className='headerContainer' style={{ width: '100%' }}>
          <Popover
            className={isFilterModalOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
            trigger='click'
            placement='bottomLeft'
            content={popOverContent}
            open={isFilterModalOpen}
            destroyTooltipOnHide
            onOpenChange={() => {
              handleCloseFilterModel();
            }}>
            <FilterButton onClick={() => setIsFilterModalOpen(true)}>
              <div className='text-base'>Filter</div>
            </FilterButton>
          </Popover>
          <div className='searchInput' style={{ width: '100%' }}>
            <SearchField
              handleOnChange={handleSearch}
              value={search}
              handlePressEnter={getSearch}
              onReset={handleSearchData}
              placeholder={'Search by Invoice ID'}
              loading={!!(isFetching && search)}
              dropdown={{ show: false }}
              enableSearchBtn={true}
            />
          </div>
        </div>
      </div>
      <br />
      <div className='overflow-x-auto custom-table' ref={tableContainerRef}>
        <Table
          loading={isLoading}
          dataSource={data?.invoices}
          columns={columnsData}
          scroll={{ x: 'auto' }}
          onRow={(record) => {
            return {
              onClick: (event) => {
                const { className } = event?.target;
                if (!className?.includes('table-action-btn')) {
                  navigate(`/invoice/${record?._id}`, {
                    state: {
                      record,
                      id: record?._id,
                      filtersData: handleStateByPassData(queryParams),
                      searchValue: search,
                    },
                  });
                }
              },
              onMouseEnter: handleRowHover,
              className: `custom-row-class ${recordId === record?._id ? 'highlighted-row' : ''}`,
            };
          }}
          totalLenght={data?.total}
          pagingation={{
            handlePageChange: handlePagePageChange,
            perPageSize: queryParams?.limit,
            page: queryParams?.currentPage,
          }}
        />
      </div>
    </div>
  );
};
export default MerchantInvoice;
